import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { APP_NAME, API_CONFIG } from '../helpers/constants';
import { forgetPassword } from '../services/authService';
import { ArrowBack, Mail } from '@mui/icons-material';

const ForgotPassword: React.FC = () => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!email) {
        setError('Please enter your email address');
        setLoading(false);
        return;
      }

      // Call the forget password API
      const response = await forgetPassword({ 
        base_url: API_CONFIG.NODE_BASE_URL,
        email_id: email,
        app_name: APP_NAME || 'Prism 7',
        template_id: 'TMPLT_PASSWORD_RESET_001' 
      });
      
      // Check for success - the API might return different success indicators
      const successMessage = response.message?.toLowerCase() || '';
      const isSuccess = response.status === 'success' || 
                       successMessage.includes('sent') || 
                       successMessage.includes('email') ||
                       successMessage.includes('password reset link sent') ||
                       successMessage.includes('reset link') ||
                       successMessage.includes('check your email');
      
      if (isSuccess) {
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to send reset email');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send reset email. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      {/* Logo above the card */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box
          component="img"
          src={isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png'}
          alt={APP_NAME || 'App'}
          sx={{
            height: 48,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          Forgot Password?
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Enter your email address and we'll send you a reset link
        </Typography>

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            icon={<Mail />}
          >
            Password reset link sent to your email address. The link will expire in 1 hour.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!success ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              placeholder="Enter your email address"
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Resend the reset link
            </Button>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link 
            to="/login" 
            style={{ 
              color: muiTheme.palette.primary.main, 
              textDecoration: 'none',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            Back to Login
          </Link>
        </Typography>
      </Paper>

      {/* Copyright below form (Accsell-style footer) */}
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mt: 3 }}
      >
        © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
      </Typography>
    </Box>
  );
};

export default ForgotPassword;
